import { useAbility } from "@casl/react";
import {
  useMutationXeroDisableIntegration,
  useMutationXeroEnableIntegration,
  useQueryXeroGetIntegrationStatus,
  useQueryXeroGetRedirectUrlToAuthPage,
} from "@gymflow/api";
import { useContext } from "react";
import { useTranslation } from "react-i18next";

import { usePortalRoutes } from "../../../../hooks";
import { AbilityContext, Subject, Verb } from "../../../../permissions";
import { useClubSettings } from "../../../../providers";
import { ApiResolverContext } from "../../../../providers/ApiResolverProvider/ApiResolverContext";
import { RouteFeature, RouteLayout } from "../../../../routes";
import useGymflowModels from "../../../../store";
import { AlertCircleIcon, Button, Spinner, Switch } from "../../../atoms";
import CustomTooltip from "../../../atoms/CustomTooltip";
import { Settings04Icon } from "../../../atoms/icons/Settings04Icon";
import { IntegrationCard } from "../components/IntegrationCard";

export const XeroIntegrationCard = () => {
  const ability = useAbility(AbilityContext);
  const { t } = useTranslation();
  const { api } = useGymflowModels();
  const { clubId } = useContext(ApiResolverContext);
  const { featureFlags, club } = useClubSettings();
  const { createClubLink, createAbsoluteClubLink } = usePortalRoutes();
  const { data: dataXero, isLoading: isLoadingXero } =
    useQueryXeroGetIntegrationStatus({
      api,
      clubId,
      refetchOnWindowFocus: true,
      enabled: !!featureFlags.PORTAL_XERO,
    });

  const { mutateAsync: mutateAsyncEnableXero } =
    useMutationXeroEnableIntegration({ api });

  const { mutateAsync: mutateAsyncDisableXero } =
    useMutationXeroDisableIntegration({ api });

  const { refetch: refetchRedirectAuthUrlXero } =
    useQueryXeroGetRedirectUrlToAuthPage({
      api,
      opts: {
        appUrl: createAbsoluteClubLink(
          RouteLayout.Staff,
          `${RouteFeature.XeroIntegration}?authenticatedToast=show`,
        ),
      },
      enabled: false,
    });
  const isXeroEnabled =
    !!dataXero?.status &&
    ["ENABLED", "ONBOARDING", "NEED_AUTHORIZATION"].includes(dataXero.status);
  return (
    <IntegrationCard
      logo={
        <div className="bg-xero-logo size-10 rounded-lg bg-contain bg-center bg-no-repeat" />
      }
      title={t("components.integrations.xero.card.title")}
      description={t("components.integrations.xero.card.description")}
      switchComponent={
        isLoadingXero ? (
          <Spinner />
        ) : (
          <Switch
            value={isXeroEnabled}
            disabled={
              club?.stripeAccountType !== "CONNECTED" ||
              !ability.can(Verb.Edit, Subject.XeroConnect)
            }
            onChange={async () => {
              if (!dataXero?.status) {
                return;
              }

              if (isXeroEnabled) {
                await mutateAsyncDisableXero();
              } else {
                await mutateAsyncEnableXero();
              }
            }}
          />
        )
      }
      footer={
        ability.can(Verb.Edit, Subject.XeroConnect) ? (
          <>
            {club?.stripeAccountType !== "CONNECTED" && (
              <CustomTooltip
                asChild
                message={t(
                  "components.integrations.xero.card.unavailable.message",
                )}
              >
                <div className="flex flex-row gap-1">
                  <AlertCircleIcon
                    className="size-5"
                    pathClassName="stroke-gray-500"
                  />
                  <div className="text-sm font-semibold text-gray-400">
                    {t("components.integrations.xero.card.unavailable.label")}
                  </div>
                </div>
              </CustomTooltip>
            )}
            {isXeroEnabled &&
              dataXero.status === "NEED_AUTHORIZATION" &&
              club?.stripeAccountType === "CONNECTED" && (
                <Button
                  className="!text-secondary-500 hover:!text-secondary-500 flex h-fit flex-row gap-1 p-0 text-sm font-semibold hover:opacity-50"
                  intent="transparent"
                  onClick={async () => {
                    const { data } = await refetchRedirectAuthUrlXero();

                    window.open(data?.authPageRedirectUrl, "_blank");
                  }}
                >
                  <Settings04Icon />
                  {t("components.integrations.xero.card.authorize")}
                </Button>
              )}
            {isXeroEnabled &&
              ["ONBOARDING", "ENABLED"].includes(dataXero.status) &&
              club?.stripeAccountType === "CONNECTED" && (
                <Button
                  link={createClubLink(
                    RouteLayout.Staff,
                    RouteFeature.XeroIntegration,
                  )}
                  className="h-fit flex-row gap-1 p-0 text-sm hover:opacity-50"
                  intent="link-secondary"
                >
                  <Settings04Icon />
                  {dataXero.status === "ENABLED"
                    ? t("components.integrations.xero.card.update")
                    : t("components.integrations.xero.card.finishSetup")}
                </Button>
              )}
          </>
        ) : undefined
      }
    />
  );
};
